// This file is auto-generated by @hey-api/openapi-ts

import type { Options as ClientOptions, TDataShape, Client } from "@hey-api/client-fetch";
import type {
  ListSessionAuditData,
  ListSessionAuditResponse,
  GetSessionAuditEntryData,
  GetSessionAuditEntryResponse,
  GetVapidPublicKeyData,
  GetVapidPublicKeyResponse,
  DeletePushSubscriptionData,
  DeletePushSubscriptionResponse,
  SavePushSubscriptionData,
  SavePushSubscriptionResponse,
  GetSessionReadinessData,
  GetSessionReadinessResponse,
  GetCodeStatusData,
  GetCodeStatusResponse,
  GenerateCodeData,
  GenerateCodeResponse,
  RegenerateCodeData,
  RegenerateCodeResponse,
  ListCodeSmsRecipientsData,
  ListCodeSmsRecipientsResponse,
  SendCodeBySmsData,
  SendCodeBySmsResponse,
  ValidateCodeData,
  ValidateCodeResponse,
  GetHealthData,
  GetHealthResponse,
  RegisterUserData,
  RegisterUserResponse,
  GetCurrentUserData,
  GetCurrentUserResponse,
  UpdateCurrentUserData,
  UpdateCurrentUserResponse,
  ListInvitationsData,
  ListInvitationsResponse,
  CreateInvitationData,
  CreateInvitationResponse,
  RevokeInvitationData,
  RevokeInvitationResponse,
  PreviewInvitationData,
  PreviewInvitationResponse,
  GetMyWardsData,
  GetMyWardsResponse,
  ListSessionsData,
  ListSessionsResponse,
  CreateSessionData,
  CreateSessionResponse,
  CreateSessionError,
  GetSessionData,
  GetSessionResponse,
  GetSessionTeamData,
  GetSessionTeamResponse,
  ListPatientsData,
  ListPatientsResponse,
  CreatePatientData,
  CreatePatientResponse,
  GetPatientData,
  GetPatientResponse,
  UpdatePatientData,
  UpdatePatientResponse,
  AcknowledgePatientData,
  AcknowledgePatientResponse,
  RecordPatientViewData,
  RecordPatientViewResponse,
  EscalatePatientData,
  EscalatePatientResponse,
  EscalateTaskData,
  EscalateTaskResponse,
  AddTaskData,
  AddTaskResponse,
  CompleteTaskData,
  CompleteTaskResponse,
  ReopenTaskData,
  ReopenTaskResponse,
  ChangePriorityData,
  ChangePriorityResponse,
  CompletePatientData,
  CompletePatientResponse,
  ReopenPatientData,
  ReopenPatientResponse,
  GetOvernightUpdateData,
  GetOvernightUpdateResponse,
  SaveOvernightUpdateData,
  SaveOvernightUpdateResponse,
  ListPatientNotesData,
  ListPatientNotesResponse,
  AddPatientNoteData,
  AddPatientNoteResponse,
  DeletePatientNoteData,
  EditPatientNoteData,
  EditPatientNoteResponse,
  DiscardPatientDraftData,
  DiscardPatientDraftResponse,
  GetPatientDraftData,
  GetPatientDraftResponse,
  SavePatientDraftData,
  SavePatientDraftResponse,
  ListWardsData,
  ListWardsResponse,
  CreateWardData,
  CreateWardResponse,
  UpdateWardData,
  UpdateWardResponse,
  ArchiveWardData,
  ArchiveWardResponse,
  ListWardMembersData,
  ListWardMembersResponse,
  AddWardMemberData,
  AddWardMemberResponse,
  RemoveWardMemberData,
  RemoveWardMemberResponse,
  UpdateWardMemberRoleData,
  UpdateWardMemberRoleResponse,
} from "./types.gen";
import { client as _heyApiClient } from "./client.gen";

export type Options<
  TData extends TDataShape = TDataShape,
  ThrowOnError extends boolean = boolean,
> = ClientOptions<TData, ThrowOnError> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client;
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: Record<string, unknown>;
};

/**
 * List audit-log entries for a session
 * Returns the session's audit trail in reverse-chronological order, paginated with an opaque cursor. Optionally narrow to one patient with `patientName` (case-insensitive substring on the denormalised patient name). Any ward member can read it; cross-ward callers see 404. The structured detail payload is not included in v1.
 */
export const listSessionAudit = <ThrowOnError extends boolean = false>(
  options: Options<ListSessionAuditData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).get<ListSessionAuditResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/audit",
    ...options,
  });
};

/**
 * Fetch one audit-log entry with its full structured detail
 * Returns a single audit row, including the structured `detail` payload omitted from the list endpoint, for the viewer's expand-on-demand view. The entry must belong to the given session; an id from another session (or an unknown id) reads as 404, the same posture as the list's cross-ward 404. The log is permanent — this read never alters the row.
 */
export const getSessionAuditEntry = <ThrowOnError extends boolean = false>(
  options: Options<GetSessionAuditEntryData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).get<GetSessionAuditEntryResponse, unknown, ThrowOnError>(
    {
      security: [
        {
          scheme: "bearer",
          type: "http",
        },
      ],
      url: "/sessions/{sessionId}/audit/{entryId}",
      ...options,
    },
  );
};

/**
 * The VAPID public key for subscribing
 * Returns the application-server VAPID public key the browser needs to create a push subscription, or null when push is not configured (dev/local).
 */
export const getVapidPublicKey = <ThrowOnError extends boolean = false>(
  options?: Options<GetVapidPublicKeyData, ThrowOnError>,
) => {
  return (options?.client ?? _heyApiClient).get<GetVapidPublicKeyResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/push/public-key",
    ...options,
  });
};

/**
 * Unregister this device from push
 * Removes the caller's subscription identified by endpoint. Removing an unknown endpoint is a no-op (still 204).
 */
export const deletePushSubscription = <ThrowOnError extends boolean = false>(
  options: Options<DeletePushSubscriptionData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).delete<
    DeletePushSubscriptionResponse,
    unknown,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/push/subscriptions",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Register this device for push
 * Upserts the caller's browser/device push subscription (keyed on its endpoint), so re-subscribing the same device is idempotent.
 */
export const savePushSubscription = <ThrowOnError extends boolean = false>(
  options: Options<SavePushSubscriptionData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<
    SavePushSubscriptionResponse,
    unknown,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/push/subscriptions",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Get the handover-readiness checklist
 * Returns per-patient completeness for the session — SBAR done, tasks present, and any blocking issues — plus an overall `ready` flag. The start-handover screen renders this as the final checklist and disables code generation until `ready` is true. Only the session's outgoing doctor may read it; the code-generation endpoint enforces the same precondition.
 */
export const getSessionReadiness = <ThrowOnError extends boolean = false>(
  options: Options<GetSessionReadinessData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).get<GetSessionReadinessResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{id}/readiness",
    ...options,
  });
};

/**
 * Get the handover code status
 * Returns the non-secret state of the session's code (whether one exists, when it expires, how many attempts remain, lockout, and whether it's been used) so the outgoing doctor can see what's going on after a page reload. Never returns the plaintext — that is unrecoverable by design. Only the session's outgoing doctor may read this.
 */
export const getCodeStatus = <ThrowOnError extends boolean = false>(
  options: Options<GetCodeStatusData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).get<GetCodeStatusResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{id}/code",
    ...options,
  });
};

/**
 * Generate the handover code
 * Generates the handover code for a draft session and flips it to active. Only the session's outgoing doctor may do this, and only while the session is a draft with at least one patient. The plaintext code is returned **once** here and never again. Not idempotent: a second call returns 409 rather than a new code.
 */
export const generateCode = <ThrowOnError extends boolean = false>(
  options: Options<GenerateCodeData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<GenerateCodeResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{id}/code",
    ...options,
  });
};

/**
 * Regenerate the handover code
 * Mints a fresh handover code for an active session, invalidating the previous one and resetting its 2-hour expiry, attempt counter and lockout. Only the session's outgoing doctor may do this, only while the session is active, and only before the incoming doctor has used the code. The new plaintext code is returned **once** here, like generation.
 */
export const regenerateCode = <ThrowOnError extends boolean = false>(
  options: Options<RegenerateCodeData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<RegenerateCodeResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{id}/code/regenerate",
    ...options,
  });
};

/**
 * List team members the code can be texted to
 * Returns the session ward's other members (name, clinical role, and whether they have a phone number on file) so the outgoing doctor can pick who to SMS the code to. Owner-only.
 */
export const listCodeSmsRecipients = <ThrowOnError extends boolean = false>(
  options: Options<ListCodeSmsRecipientsData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).get<
    ListCodeSmsRecipientsResponse,
    unknown,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{id}/code/sms/recipients",
    ...options,
  });
};

/**
 * Text the handover code to a team member
 * Sends the active handover code by SMS to a chosen ward member who has a phone number. Only the session's outgoing doctor may do this. The caller re-supplies the code on screen; it is verified against the session before sending. Delivery is best-effort — a provider failure returns `sent: false` (the code stays on screen), while invalid requests are 4xx.
 */
export const sendCodeBySms = <ThrowOnError extends boolean = false>(
  options: Options<SendCodeBySmsData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<SendCodeBySmsResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{id}/code/sms",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Validate a handover code
 * The incoming doctor submits the handover code (`WRD-XXX` or `WRDXXX`). On success they are recorded as the session's incoming doctor and the code is consumed. Five wrong attempts lock the code for 10 minutes (423). Expired/used codes return 410; the outgoing doctor validating their own code returns 409.
 */
export const validateCode = <ThrowOnError extends boolean = false>(
  options: Options<ValidateCodeData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<ValidateCodeResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/validate-code",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Liveness probe
 * Returns 200 with the running version + commit SHA. Used by Cloud Run, uptime monitors, and humans.
 */
export const getHealth = <ThrowOnError extends boolean = false>(
  options?: Options<GetHealthData, ThrowOnError>,
) => {
  return (options?.client ?? _heyApiClient).get<GetHealthResponse, unknown, ThrowOnError>({
    url: "/health",
    ...options,
  });
};

/**
 * Register a new doctor
 * Creates a Firebase user, stores GMC + role as custom claims, inserts the application user row, and returns a Firebase custom token the SPA exchanges via signInWithCustomToken().
 */
export const registerUser = <ThrowOnError extends boolean = false>(
  options: Options<RegisterUserData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<RegisterUserResponse, unknown, ThrowOnError>({
    url: "/auth/register",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Get the authenticated user
 * Returns the application user resolved from the Firebase ID token on the Authorization header.
 */
export const getCurrentUser = <ThrowOnError extends boolean = false>(
  options?: Options<GetCurrentUserData, ThrowOnError>,
) => {
  return (options?.client ?? _heyApiClient).get<GetCurrentUserResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/auth/me",
    ...options,
  });
};

/**
 * Update the authenticated user's profile
 * Updates the caller's own profile. Currently the optional E.164 phone number used to SMS them a handover code (HAN-66): a valid number sets it, null clears it. Returns the updated user.
 */
export const updateCurrentUser = <ThrowOnError extends boolean = false>(
  options: Options<UpdateCurrentUserData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).patch<UpdateCurrentUserResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/auth/me",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * List a ward's pending invitations
 * Returns the ward's pending (not yet accepted/revoked/expired) invitations.
 */
export const listInvitations = <ThrowOnError extends boolean = false>(
  options: Options<ListInvitationsData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).get<ListInvitationsResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/wards/{wardId}/invitations",
    ...options,
  });
};

/**
 * Invite an email to a ward
 * Records a pending invitation (ward, role, inviter, hashed token, 7-day expiry) and sends the invite email via Resend. The invite pre-authorizes membership — it never creates an account; the invitee completes their own registration and is auto-joined on redeem. Inviting an email that already has a live pending invite for this ward returns that invite (no duplicate).
 */
export const createInvitation = <ThrowOnError extends boolean = false>(
  options: Options<CreateInvitationData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<CreateInvitationResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/wards/{wardId}/invitations",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Revoke a pending invitation
 * Revokes a pending invitation so its link no longer works. Revoking an already-revoked or expired invite is idempotent; an already-accepted invite is a 409.
 */
export const revokeInvitation = <ThrowOnError extends boolean = false>(
  options: Options<RevokeInvitationData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).delete<RevokeInvitationResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/wards/{wardId}/invitations/{invitationId}",
    ...options,
  });
};

/**
 * Resolve an invitation accept token
 * Validates an accept-link token and returns the invitation preview (ward, email, role, status). A pending invite past its expiry reports status 'expired'. Public — keyed by the token.
 */
export const previewInvitation = <ThrowOnError extends boolean = false>(
  options: Options<PreviewInvitationData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).get<PreviewInvitationResponse, unknown, ThrowOnError>({
    url: "/invitations/{token}",
    ...options,
  });
};

/**
 * List the current user's wards
 * Returns the wards the authenticated user is a member of — the source for the post-login ward picker.
 */
export const getMyWards = <ThrowOnError extends boolean = false>(
  options?: Options<GetMyWardsData, ThrowOnError>,
) => {
  return (options?.client ?? _heyApiClient).get<GetMyWardsResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/me/wards",
    ...options,
  });
};

/**
 * List handover sessions
 * Lists sessions on wards the caller belongs to, newest first. Optional filters: mine=true restricts to sessions the caller is a participant in (outgoing or incoming doctor); status is a comma-separated list (e.g. draft,active). Powers the home screen's "continue a handover" list.
 */
export const listSessions = <ThrowOnError extends boolean = false>(
  options?: Options<ListSessionsData, ThrowOnError>,
) => {
  return (options?.client ?? _heyApiClient).get<ListSessionsResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions",
    ...options,
  });
};

/**
 * Start a handover session
 * Creates a draft handover session for a ward the caller belongs to. If an unfinished session already exists for the ward, returns 409 with its summary — unless force=true, which locks the old one and starts a new one. The code is generated later (E6).
 */
export const createSession = <ThrowOnError extends boolean = false>(
  options: Options<CreateSessionData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<
    CreateSessionResponse,
    CreateSessionError,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Get a handover session
 * Returns the session if the caller is a member of its ward; 404 otherwise (a session on a ward the caller cannot access is indistinguishable from one that does not exist).
 */
export const getSession = <ThrowOnError extends boolean = false>(
  options: Options<GetSessionData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).get<GetSessionResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{id}",
    ...options,
  });
};

/**
 * List doctors on the session's ward
 * The 'assign to' dropdown source for the patient form — ward members only.
 */
export const getSessionTeam = <ThrowOnError extends boolean = false>(
  options: Options<GetSessionTeamData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).get<GetSessionTeamResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/team",
    ...options,
  });
};

/**
 * List the session's patients
 * Every patient on the session, each with its tasks. Readable by any ward member.
 */
export const listPatients = <ThrowOnError extends boolean = false>(
  options: Options<ListPatientsData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).get<ListPatientsResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patients",
    ...options,
  });
};

/**
 * Add a patient to the session
 * Creates a patient and its tasks in one transaction, writing the patient.created and task.added audit entries. Only the session's outgoing doctor may do this, and only while the session is unlocked.
 */
export const createPatient = <ThrowOnError extends boolean = false>(
  options: Options<CreatePatientData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<CreatePatientResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patients",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Get a patient
 * A single patient with its tasks. Readable by any ward member.
 */
export const getPatient = <ThrowOnError extends boolean = false>(
  options: Options<GetPatientData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).get<GetPatientResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patients/{patientId}",
    ...options,
  });
};

/**
 * Update a patient
 * Replaces the patient and its task list wholesale, writing a patient.updated audit entry with a field diff. Refused (423) if the patient is acknowledged or the session is locked; only the outgoing doctor may do it.
 */
export const updatePatient = <ThrowOnError extends boolean = false>(
  options: Options<UpdatePatientData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).patch<UpdatePatientResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patients/{patientId}",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Acknowledge a patient (incoming doctor's clinical-responsibility sign-off)
 * The incoming doctor records that they have received the handover for this patient and accepted clinical responsibility. The GMC is snapshotted into the row so a later user rename can't rewrite history. Acknowledging the last unacknowledged patient also locks the entire session, after which no further edits are accepted.
 */
export const acknowledgePatient = <ThrowOnError extends boolean = false>(
  options: Options<AcknowledgePatientData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<AcknowledgePatientResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patients/{patientId}/acknowledge",
    ...options,
  });
};

/**
 * Record that the incoming doctor has viewed a patient
 * The incoming doctor opened this patient's detail during the handover. Backs the 'viewed' indicator that ensures no patient is skipped (spec §8). Idempotent: re-opening the same patient is a no-op that returns the original viewedAt and writes no further audit entry. Only the session's incoming doctor may record a view, and only while the session is live.
 */
export const recordPatientView = <ThrowOnError extends boolean = false>(
  options: Options<RecordPatientViewData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<RecordPatientViewResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patients/{patientId}/view",
    ...options,
  });
};

/**
 * Escalate a whole patient to the senior clinician(s) on the ward
 * Whole-patient emergency escalation: notifies the most-senior available ward member by clinical role (consultant > registrar) via push — with the patient name, bed, and the one-line SBAR Situation — and flags the patient as escalated. One-shot: re-escalating a patient is a 409. Any ward member may escalate.
 */
export const escalatePatient = <ThrowOnError extends boolean = false>(
  options: Options<EscalatePatientData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<EscalatePatientResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patients/{patientId}/escalate",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Escalate a task to the senior clinician(s) on the ward
 * Notifies the most-senior available ward member by clinical role (consultant > registrar) via push and flags the task as escalated. One-shot: re-escalating a task is a 409. Any ward member may escalate.
 */
export const escalateTask = <ThrowOnError extends boolean = false>(
  options: Options<EscalateTaskData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<EscalateTaskResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patients/{patientId}/tasks/{taskId}/escalate",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Add a task to a patient
 */
export const addTask = <ThrowOnError extends boolean = false>(
  options: Options<AddTaskData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<AddTaskResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patients/{patientId}/tasks",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Mark a task done
 */
export const completeTask = <ThrowOnError extends boolean = false>(
  options: Options<CompleteTaskData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<CompleteTaskResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patients/{patientId}/tasks/{taskId}/complete",
    ...options,
  });
};

/**
 * Re-open a completed task
 */
export const reopenTask = <ThrowOnError extends boolean = false>(
  options: Options<ReopenTaskData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<ReopenTaskResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patients/{patientId}/tasks/{taskId}/reopen",
    ...options,
  });
};

/**
 * Change a patient's priority
 */
export const changePriority = <ThrowOnError extends boolean = false>(
  options: Options<ChangePriorityData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).patch<ChangePriorityResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patients/{patientId}/priority",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Mark a patient complete (archive)
 */
export const completePatient = <ThrowOnError extends boolean = false>(
  options: Options<CompletePatientData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<CompletePatientResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patients/{patientId}/complete",
    ...options,
  });
};

/**
 * Re-open a completed patient
 */
export const reopenPatient = <ThrowOnError extends boolean = false>(
  options: Options<ReopenPatientData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<ReopenPatientResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patients/{patientId}/reopen",
    ...options,
  });
};

/**
 * Get a patient's overnight update
 * Returns the saved end-of-shift overnight update for this patient, or null if none has been saved yet. The form pre-populates from this and shows the patient's original SBAR read-only alongside it. Readable by any ward member.
 */
export const getOvernightUpdate = <ThrowOnError extends boolean = false>(
  options: Options<GetOvernightUpdateData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).get<GetOvernightUpdateResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patients/{patientId}/overnight-update",
    ...options,
  });
};

/**
 * Save a patient's overnight update (incoming doctor)
 * Records what happened overnight, what was done, and (optionally) what the day team needs to know — feeding the next shift's morning handover. Upsert: one update per patient, so re-saving overwrites it. Only the session's incoming doctor may save. Writes an 'Overnight update saved' audit entry. Allowed even after the session locks (the update is recorded during the shift, after the handover has been received).
 */
export const saveOvernightUpdate = <ThrowOnError extends boolean = false>(
  options: Options<SaveOvernightUpdateData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).put<SaveOvernightUpdateResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patients/{patientId}/overnight-update",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * List a patient's notes (oldest first)
 */
export const listPatientNotes = <ThrowOnError extends boolean = false>(
  options: Options<ListPatientNotesData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).get<ListPatientNotesResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patients/{patientId}/notes",
    ...options,
  });
};

/**
 * Add a note to a patient
 */
export const addPatientNote = <ThrowOnError extends boolean = false>(
  options: Options<AddPatientNoteData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<AddPatientNoteResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patients/{patientId}/notes",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Delete your own note
 */
export const deletePatientNote = <ThrowOnError extends boolean = false>(
  options: Options<DeletePatientNoteData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).delete<unknown, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patients/{patientId}/notes/{noteId}",
    ...options,
  });
};

/**
 * Edit your own note
 */
export const editPatientNote = <ThrowOnError extends boolean = false>(
  options: Options<EditPatientNoteData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).patch<EditPatientNoteResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patients/{patientId}/notes/{noteId}",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Discard the caller's patient-form draft
 * Deletes the caller's draft for this form — called after a successful submit so a stale draft never re-restores. Idempotent: discarding a draft that doesn't exist is a no-op (still 204).
 */
export const discardPatientDraft = <ThrowOnError extends boolean = false>(
  options: Options<DiscardPatientDraftData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).delete<
    DiscardPatientDraftResponse,
    unknown,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patient-draft",
    ...options,
  });
};

/**
 * Get the caller's auto-saved patient-form draft
 * Returns the caller's saved draft for the Add/Edit Patient form, or null if none has been saved yet — the form restores from it on reopen. Omit `patientId` for the new-patient (create) draft; pass it for an existing patient's edit draft. Scoped to the caller: never returns another doctor's draft.
 */
export const getPatientDraft = <ThrowOnError extends boolean = false>(
  options: Options<GetPatientDraftData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).get<GetPatientDraftResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patient-draft",
    ...options,
  });
};

/**
 * Auto-save the caller's patient-form draft (outgoing doctor)
 * Upserts the caller's draft of the Add/Edit Patient form. The body's `formData` is the partial form values, stored opaque and deliberately unvalidated — a draft may be incomplete, so this never enforces the SBAR/priority rules the real Save does. Idempotent: re-saving overwrites the same draft and bumps its timestamp. Only the session's outgoing doctor may save.
 */
export const savePatientDraft = <ThrowOnError extends boolean = false>(
  options: Options<SavePatientDraftData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).put<SavePatientDraftResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/sessions/{sessionId}/patient-draft",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * List all wards (super-admin)
 * Returns every ward, including archived ones, ordered by name.
 */
export const listWards = <ThrowOnError extends boolean = false>(
  options?: Options<ListWardsData, ThrowOnError>,
) => {
  return (options?.client ?? _heyApiClient).get<ListWardsResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/admin/wards",
    ...options,
  });
};

/**
 * Create a ward
 * Creates a ward. Short code must be a unique 3-character handover-code prefix.
 */
export const createWard = <ThrowOnError extends boolean = false>(
  options: Options<CreateWardData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<CreateWardResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/admin/wards",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Edit a ward
 * Edits a ward's name and/or short code. At least one field must be provided.
 */
export const updateWard = <ThrowOnError extends boolean = false>(
  options: Options<UpdateWardData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).patch<UpdateWardResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/admin/wards/{wardId}",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Archive a ward
 * Soft-archives a ward: it's hidden from ward-selection but kept for history/audit, since sessions and memberships reference it. Archiving an already-archived ward is a 409.
 */
export const archiveWard = <ThrowOnError extends boolean = false>(
  options: Options<ArchiveWardData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<ArchiveWardResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/admin/wards/{wardId}/archive",
    ...options,
  });
};

/**
 * List a ward's members
 * Returns the ward's roster: each member's name, email, GMC, clinical role, membership role, and status.
 */
export const listWardMembers = <ThrowOnError extends boolean = false>(
  options: Options<ListWardMembersData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).get<ListWardMembersResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/wards/{wardId}/members",
    ...options,
  });
};

/**
 * Add an existing user to a ward
 * Adds an already-registered user (by email or GMC) to the ward as a member. 404 if no such user exists — the caller then falls back to an email invite. Adding an existing member is idempotent.
 */
export const addWardMember = <ThrowOnError extends boolean = false>(
  options: Options<AddWardMemberData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<AddWardMemberResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/wards/{wardId}/members",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Remove a member from a ward
 * Removes a member. Removing the ward's last remaining admin is a 409.
 */
export const removeWardMember = <ThrowOnError extends boolean = false>(
  options: Options<RemoveWardMemberData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).delete<RemoveWardMemberResponse, unknown, ThrowOnError>({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/wards/{wardId}/members/{userId}",
    ...options,
  });
};

/**
 * Change a member's role
 * Sets a member's membership role (member/admin). Demoting the ward's last remaining admin is a 409.
 */
export const updateWardMemberRole = <ThrowOnError extends boolean = false>(
  options: Options<UpdateWardMemberRoleData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).patch<
    UpdateWardMemberRoleResponse,
    unknown,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/wards/{wardId}/members/{userId}",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};
